<template>
  <vs-sidebar
    position-right
    :parent="parentClass"
    default-index="1"
    color="primary"
    class="sidebarx"
    spacer
    v-model="handleActive"
  >
    <div class="px-5">
      <vs-collapse type="border" accordion>
        <vs-collapse-item-m
          :hidden="true"
          open
          v-if="supportEmbedded.includes(leaftype)"
        >
          <div slot="header">
            <h3>{{ id ? `Edit ${leaftype}` : `Create ${leaftype}` }}</h3>
          </div>
          <div v-if="leaftype === 'video'">
            <video-form
              :embedded="true"
              :onClick="saveVideo"
              @prevent-close="$emit('prevent-close', $event)"
            />
          </div>

          <div v-if="leaftype === 'quiz'">
            <quiz-form :embedded="true" :onClick="saveQuiz" />
          </div>

          <div v-if="leaftype === 'lab'">
            <vs-tabs alignment="fixed">
              <vs-tab class="pt-8" label="Basic information">
                <lab-form :embedded="true" :onClick="saveLab" />
              </vs-tab>
              <vs-tab class="pt-8" label="Objectives">
                <lab-objectives />
              </vs-tab>
            </vs-tabs>
          </div>

          <div v-if="leaftype === 'iframe'">
            <i-frame-form
              :embedded="true"
              :onClick="saveIFrame"
              @prevent-close="$emit('prevent-close', $event)"
            />
          </div>

          <div v-if="leaftype === 'exercise'">
            <exercise-form
              :embedded="true"
              :onClick="saveExercise"
              @prevent-close="$emit('prevent-close', $event)"
            />
          </div>
        </vs-collapse-item-m>
        <vs-collapse-item-m v-if="!id || !supportEmbedded.includes(leaftype)">
          <div slot="header">Select {{ leaftype }}</div>
          <content-list
            :withSelection="true"
            :filterTypes="typeCapitalized"
            @select-item="selectItem"
            :videoOptions="videoOptions"
          />
        </vs-collapse-item-m>
      </vs-collapse>
    </div>
  </vs-sidebar>
</template>

<script>
import QuizForm from './QuizFormContent'
import VideoForm from './VideoFormContent'
import LabForm from './LabFormContent'
import LabObjectives from './LabObjectivesContent'
import IFrameForm from './IFrameFormContent'
import ExerciseForm from './exercises/ExerciseFormContent'
import ContentList from '../ContentList'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'editLeaf',
  components: {
    QuizForm,
    VideoForm,
    LabForm,
    LabObjectives,
    ContentList,
    IFrameForm,
    ExerciseForm,
  },
  props: {
    leaftype: String,
    id: String,
    active: Boolean,
    parentClass: {
      type: String,
      required: false,
      default: '.course-form'
    },
    isCourseContext: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      supportEmbedded: ['video', 'quiz', 'lab', 'iframe', 'exercise'],
      videoOptions: ['lesson', 'ine_live'],
    }
  },
  computed: {
    ...mapGetters('quizzes', ['getCurrentQuiz']),
    ...mapGetters('videos', ['getCurrentVideo']),
    ...mapGetters('labs', ['getCurrentLab']),
    ...mapGetters('iframes', ['getCurrentIFrame']),
    ...mapGetters('exercises', ['getCurrentExercise']),
    ...mapGetters('taxonomy', ['getLabTaxonomy', 'getVideoTaxonomy']),
    handleActive: {
      get() {
        return this.active
      },
      set(value) {
        this.$emit('change-sidebar', value)
      },
    },
    typeCapitalized() {
      if (this.leaftype === 'iframe') return ['iFrame']
      return this.leaftype
        ? [this.leaftype.charAt(0).toUpperCase() + this.leaftype.slice(1)]
        : []
    },
  },
  methods: {
    ...mapActions('quizzes', [
      'getQuizAction',
      'saveQuizToApi',
      'saveQuizToEdit',
      'saveQuizToEntries',
    ]),
    ...mapActions('videos', [
      'getVideoAction',
      'saveVideoToApi',
      'saveVideoToEdit',
      'saveVideoToEntries',
      'assignTaxonomiesToVideo',
    ]),
    ...mapActions('labs', [
      'getLabAction',
      'saveLabToApi',
      'saveLabToEdit',
      'saveLabToEntries',
      'assignTaxonomiesToLab',
    ]),
    ...mapActions('iframes', [
      'getIFrameAction',
      'saveIFrameToApi',
      'saveIFrameToEdit',
      'saveIFrameToEntries',
    ]),
    ...mapActions('exercises', [
      'getExerciseAction',
      'saveExerciseToApi',
      'saveExerciseToEdit',
      'saveExerciseToEntries',
    ]),
    async saveQuiz() {
      try {
        this.$vs.loading()

        await this.saveQuizToApi()

        //update quiz in quiz store
        this.saveQuizToEntries({ ...this.getCurrentQuiz })

        //Quiz will be in the getCurrentQuiz
        if (!this.id)
          this.$emit('add-to-structure', {
            type: 'quiz',
            item: this.getCurrentQuiz,
          })

        this.$vs.loading.close()
        this.$vs.notify({
          title: 'Success',
          text: 'Quiz saved',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success',
        })
      } catch (error) {
        this.resetByErrorLoading(error)
      }
    },
    async saveTaxonomies(contentType, contentId) {
      try {  
        switch (contentType) {
          case 'video':
            if (this.getVideoTaxonomy && this.getVideoTaxonomy.contentId !== contentId) {
              await this.assignTaxonomiesToVideo({
                contentId: contentId,
                addedSkills: [],
              })
              break
            }
            await this.assignTaxonomiesToVideo({
              contentId: contentId,
              addedSkills: this.getVideoTaxonomy.addedSkills,
            })
            break
          case 'lab':
            if (!this.getLabTaxonomy || this.getLabTaxonomy.contentId !== contentId) {
              await this.assignTaxonomiesToLab({
                contentId: contentId,
                addedSkills: [],
              })
              break
            }
            await this.assignTaxonomiesToLab({
              contentId: contentId,
              addedSkills: this.getLabTaxonomy.addedSkills,
            })
            break
          default:
            break
        }
      } catch (error) {
        this.showError(error)
      }
    },
    async saveVideo() {
      try {
        this.$vs.loading()

        await this.saveVideoToApi()

        //update video in video store
        this.saveVideoToEntries({ ...this.getCurrentVideo })

        // Emit this only if we are creating or adding..
        if (!this.id)
          this.$emit('add-to-structure', {
            type: 'video',
            item: this.getCurrentVideo,
          })

        if (this.getCurrentVideo.id) {
          await this.saveTaxonomies('video', this.getCurrentVideo.id)
        }

        this.$vs.loading.close()
        this.$vs.notify({
          title: 'Success',
          text: 'Video saved',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success',
        })
      } catch (error) {
        this.resetByErrorLoading(error)
      }
    },
    async saveLab() {
      // For new SD labs, warn before saving to avoid orphan labs on cancel
      if (!this.id && this.getCurrentLab.is_lab_experience && this.isCourseContext) {
        this.$vs.dialog({
          color: 'warning',
          title: 'Warning',
          text: 'You are adding an SD lab to this course. This means all learners who have access to the course will automatically gain access to this SD lab. Please confirm this is intentional.',
          accept: () => {
            this.persistAndAddLab()
          },
        })
        return
      }

      await this.persistAndAddLab()
    },
    async persistAndAddLab() {
      try {
        this.$vs.loading()

        await this.saveLabToApi()

        //update lab in lab store
        this.saveLabToEntries({ ...this.getCurrentLab })

        if (this.getCurrentLab.id) {
          await this.saveTaxonomies('lab', this.getCurrentLab.id)
        }

        this.$vs.loading.close()
        this.$vs.notify({
          title: 'Success',
          text: 'Lab saved',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success',
        })

        // Emit this only if we are creating or adding..
        if (!this.id) {
          this.$emit('add-to-structure', {
            type: 'lab',
            item: this.getCurrentLab,
          })
        }
      } catch (error) {
        this.resetByErrorLoading(error)
      }
    },
    async saveIFrame() {
      try {
        this.$vs.loading()

        await this.saveIFrameToApi()

        //update iframe in iframes store
        this.saveIFrameToEntries({ ...this.getCurrentIFrame })

        // Emit this only if we are creating or adding..
        if (!this.id)
          this.$emit('add-to-structure', {
            type: 'iframe',
            item: this.getCurrentIFrame,
          })

        this.$vs.loading.close()
        this.$vs.notify({
          title: 'Success',
          text: 'iFrame saved',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success',
        })
      } catch (error) {
        this.resetByErrorLoading(error)
      }
    },
    async saveExercise() {
      try {
        this.$vs.loading()

        await this.saveExerciseToApi()

        //update exercise in exercise store
        this.saveExerciseToEntries({ ...this.getCurrentExercise })

        // Emit this only if we are creating or adding..
        if (!this.id)
          this.$emit('add-to-structure', {
            type: 'exercise',
            item: this.getCurrentExercise,
          })

        this.$vs.loading.close()
        this.$vs.notify({
          title: 'Success',
          text: 'Exercise saved',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success',
        })
      } catch (error) {
        this.resetByErrorLoading(error)
      }
    },
    resetByErrorLoading(error) {
      this.$vs.loading.close()
      this.showError(error)
    },
    selectItem(e) {
      switch (this.leaftype) {
        case 'video':
          this.saveVideoToEdit(e.item)
          this.$emit('add-to-structure', e)
          break

        case 'quiz':
          this.saveQuizToEdit(e.item)
          this.$emit('add-to-structure', e)
          break

        case 'lab':
          if (e.item.is_skill_check && this.isCourseContext && e.item.metadata && e.item.metadata.courses && e.item.metadata.courses.length) {
            this.$vs.dialog({
              color: 'warning',
              title: 'Warning',
              text: 'This Skill Check lab is already included in another course. Reusing it may lead to confusion in how CPE credits are calculated or awarded. Please confirm that this duplication is intentional.',
              accept: () => {
                this.addLabWithSDCheck(e)
              },
            })
          } else {
            this.addLabWithSDCheck(e)
          }
          break

        case 'iframe':
          this.saveIFrameToEdit(e.item)
          this.$emit('add-to-structure', e)
          break

        case 'exercise':
          this.saveExerciseToEdit(e.item)
          this.$emit('add-to-structure', e)
          break

        default:
          this.$emit('add-to-structure', e)
          break
      }
    },
    addLabWithSDCheck(e) {
      if (e.item.is_lab_experience && this.isCourseContext) {
        this.$vs.dialog({
          color: 'warning',
          title: 'Warning',
          text: 'You are adding an SD lab to this course. This means all learners who have access to the course will automatically gain access to this SD lab. Please confirm this is intentional.',
          accept: () => {
            this.saveLabToEdit(e.item)
            this.$emit('add-to-structure', e)
          },
        })
      } else {
        this.saveLabToEdit(e.item)
        this.$emit('add-to-structure', e)
      }
    },
  },
}
</script>
